'use client'
import Footer from '@/src/components/commons/Footer/Footer'
import styles from './Blogs.module.scss'
import { useEffect, useState, type KeyboardEvent } from 'react'
import {
  BlogEntity,
  BlogGraphQLResponse,
  SearchInputProps,
  SearchResultProps,
} from './Blogs.interface'
import { useQuery } from '@apollo/client/react'
import { BLOG_SEARCH_QUERY } from './Blogs.graphql'
import { PaginationControls } from '@/src/components/commons/generics/Pagination/Pagination'
import Calender from '@/public/assets/images/calender.svg'
import Person from '@/public/assets/images/person.svg'
import Close from '@/public/assets/images/close_red_icon.svg'
import Image from 'next/image'
import { getFormattedDate } from '@/src/utils/common'
import Link from 'next/link'
import Skeleton from '@/src/components/commons/generics/Skeleton/Skeleton'
import ClearableTextInput from '@/src/components/commons/generics/ClearableTextInput/ClearableTextInput'
import { useSearchParams } from 'next/navigation'

const Blogs = () => {
  const searchParams = useSearchParams()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput)
  const [author, setAuthor] = useState('')
  const [offset, setOffset] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (searchParams) {
      if (searchParams.get('authors')) {
        setAuthor(decodeURIComponent(searchParams.get('authors')))
      } else if (searchParams.get('tag')) {
        setSearchInput(decodeURIComponent(searchParams.get('tag')))
      }
    }
  }, [searchParams])

  const { data, loading, error } = useQuery<BlogGraphQLResponse>(BLOG_SEARCH_QUERY, {
    variables: { limit: 12, offset: offset, search: debouncedSearch, author: author },
  })

  const resetInfo = () => {
    setAuthor('')
    setSearchInput('')
    setCurrentPage(1)
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setCurrentPage(1)
      setDebouncedSearch(searchInput)
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [searchInput])

  useEffect(() => {
    setOffset((currentPage - 1) * 12)
  }, [currentPage])

  if (error) {
    return (
      <p className="text-[20px] text-white py-[70px] px-[24px] w-full text-center text-[18px]">
        Unable to load data ...
      </p>
    )
  }

  if (data || loading) {
    return (
      <>
        <div className={`${styles.BlogsContainer} flex justify-center`}>
          <div className={`${styles.BlogInnerCont} w-full h-full py-[70px] px-[24px] lg:px-0`}>
            <Heading />
            <SearchInput
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              author={author}
              resetInfo={resetInfo}
            />
            {loading && <CardLoader />}
            {data?.blogsGraphql?.count === 0 && (
              <p className="text-[20px] text-white">No data ...</p>
            )}
            {data && (
              <>
                <SearchResults
                  data={data?.blogsGraphql?.entities}
                  setAuthor={setAuthor}
                  resetInfo={resetInfo}
                />
                <div className="flex justify-center items-center pt-[50px]">
                  <PaginationControls
                    totalPages={Math.ceil(data?.blogsGraphql?.count / 12)}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <Footer />
      </>
    )
  }
}

export default Blogs

const Heading = () => {
  const title = 'Research Blogs'
  const subTitle = 'News and views from the leading voice in cloud security.'
  return (
    <>
      <p className="text-white text-[42px] font-[400] leading-[46px] tracking-[-2px] pb-[20px] font-dm-mono">
        {title}
      </p>
      <p className="text-white text-[20px] font-[400] leading-[30px] tracking-[0.4px] opacity-70 font-gt-haptik">
        {subTitle}
      </p>
      <div className={`${styles.line} w-full h-[1px] mt-[40px]`}></div>
    </>
  )
}

const SearchInput = ({ searchInput, setSearchInput, author, resetInfo }: SearchInputProps) => {
  const handleClearAuthor = () => {
    resetInfo()
  }

  const handleClearAuthorKeyDown = (event: KeyboardEvent<HTMLParagraphElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    handleClearAuthor()
  }

  return (
    <div className="mt-[30px] mb-[30px]">
      {author ? (
        <div
          className={`${styles.searchInput} w-full h-[44px] px-[10px] py-[15px] flex justify-between items-center font-gt-haptik `}
        >
          <p>{author}</p>
          <p
            role="button"
            tabIndex={0}
            aria-label={`Clear author filter: ${author}`}
            className="cursor-pointer"
            onClick={handleClearAuthor}
            onKeyDown={handleClearAuthorKeyDown}
          >
            <Image src={Close} alt="" aria-hidden="true" />
          </p>
        </div>
      ) : (
        <ClearableTextInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search blogs"
          inputClassName={`${styles.searchInput} w-full h-[44px] px-[10px] py-[15px] font-gt-haptik`}
          clearButtonAriaLabel="Clear search filter"
          autoFocus
        />
      )}
    </div>
  )
}

const SearchResults = ({ data, setAuthor, resetInfo }: SearchResultProps) => {
  return (
    <div className="grid gap-[20px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {data?.map((item: BlogEntity) => (
        <Card key={item?.nid} cardInfo={item} setAuthor={setAuthor} resetInfo={resetInfo} />
      ))}
    </div>
  )
}

const Card = ({
  cardInfo,
  setAuthor,
  resetInfo,
}: {
  cardInfo: BlogEntity
  setAuthor: (author: string) => void
  resetInfo: () => void
}) => {
  const blogHref = cardInfo?.path?.alias || '/'
  const authorName = cardInfo?.entityOwner?.fieldName

  return (
    <div className={`${styles.card} flex flex-col font-dm-mono`}>
      <Link href={blogHref} className="flex flex-col cursor-pointer">
        <div
          className={`${styles?.imageBg} w-full h-[200px] flex items-center justify-center overflow-hidden relative`}
        >
          <Image
            src={cardInfo?.fieldCoverImage?.entity?.url}
            alt={cardInfo?.fieldCoverImage?.alt}
            fill
            className="w-full h-full object-cover"
          />
          <p className={`${styles?.tag} px-[8px] py-[5px] absolute bottom-0 left-[20px]`}>
            <span className={`text-[14px] font-[500] leading-[17.5px] tracking-[1.4px] uppercase`}>
              {cardInfo?.fieldBlogMainCategory?.entity?.name}
            </span>
          </p>
        </div>
        <div className={`${styles?.cardTextDiv} p-[20px] flex flex-col gap-[15px]`}>
          <p className="flex gap-[13px] items-center">
            <Image src={Calender} alt="" />
            <span
              className={`${styles?.date} text-[14px] font-[400] leading-[17.5px] tracking-[1.4px] uppercase`}
            >
              {getFormattedDate(cardInfo?.publishedAt?.value)}
            </span>
          </p>
          <p
            className={`${styles.title} text-white text-[16px] font-[400] leading-[24px] tracking-[-0.8px] text-wrap ellipsis-2-lines`}
          >
            {cardInfo?.title}
          </p>
        </div>
      </Link>
      <div className="px-[20px] pb-[20px] min-h-[36.5px] bg-[#29333f]">
        <button
          type="button"
          className="flex gap-[10px] text-[14px] font-[400] leading-[21px] tracking-[0.28px] items-center border-0 bg-transparent p-0 cursor-pointer"
          onClick={() => {
            resetInfo()
            setAuthor(authorName)
          }}
          aria-label={`Filter blogs by ${authorName}`}
        >
          <Image src={Person} alt="" />
          <span className={styles?.name}>{authorName}</span>
        </button>
      </div>
    </div>
  )
}

const CardLoader = () => {
  return (
    <div className="grid gap-[20px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 9 }).map((item, i: number) => (
        <div key={i} className="flex flex-col">
          <Skeleton height="12rem" count={1} />
          <Skeleton height="1rem" count={1} width="50%" />
          <Skeleton height="1rem" count={1} />
          <Skeleton height="1rem" count={1} width="60%" />
        </div>
      ))}
    </div>
  )
}
